'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import Image from 'next/image'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  FileIcon,
  ImageIcon,
  MoreHorizontalIcon,
  NewspaperIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DeleteConfirmationDialog, BlogPostsFilters, LoadingState, SortableHeader } from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import {
  formatDateTime,
  getCountryCodeFromLocale,
  getLocalizedValue,
  getResizedPictureUrl,
  textLimit
} from '@/lib/utils'
import { useBlogPosts, useDeleteBlogPost, useBulkDeleteBlogPosts } from '@/hooks'
import { SortEnum } from '@/enums'
import { BlogPostsFilterParams } from '@/types/blog-post'
import { useBlogPostsStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteBlogPostId, setDeleteBlogPostId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // Blog Post selection hooks from Zustand store
  const selectedBlogPostIds = useBlogPostsStore(state => state.selectedBlogPostIds)
  const toggleBlogPostSelection = useBlogPostsStore(state => state.toggleBlogPostSelection)
  const selectAllBlogPosts = useBlogPostsStore(state => state.selectAllBlogPosts)
  const deselectAllBlogPosts = useBlogPostsStore(state => state.deselectAllBlogPosts)
  const isBlogPostSelected = useBlogPostsStore(state => state.isBlogPostSelected)

  const { metaState } = useMetaStore()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'BLOG_POSTS', 'CREATE')
  const canShow = hasPermission(currentUser, 'BLOG_POSTS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'BLOG_POSTS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'BLOG_POSTS', 'DELETE')
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { mutate: deleteBlogPostMutate, isPending: isDeleting } = useDeleteBlogPost()
  const { mutate: bulkDeleteBlogPostsMutate, isPending: isBulkDeleting } = useBulkDeleteBlogPosts()

  const filters = useMemo<BlogPostsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      isBlocked: params.get('isBlocked') === 'true' ? true : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: blogPostsData, isLoading, error, refetch, isFetching } = useBlogPosts(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<BlogPostsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<BlogPostsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/blog-posts')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/blog-posts/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/blog-posts/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteBlogPostId(id)
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(id)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('blog_post_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(id)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_blog_post')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(id)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteBlogPost = () => {
    if (deleteBlogPostId) {
      deleteBlogPostMutate(deleteBlogPostId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
          toast.success(t('deleted_successfully'))
          setDeleteBlogPostId(null)
        },
        onError: error => {
          console.error('Error deleting blog post:', error)
          toast.error(t('error_deleting'))
          setDeleteBlogPostId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedBlogPostIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteBlogPosts = () => {
    if (selectedBlogPostIds.length > 0) {
      bulkDeleteBlogPostsMutate(selectedBlogPostIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (blogPostsData && blogPostsData.items.length > 0) {
      if (selectedBlogPostIds.length === blogPostsData.items.length) {
        // If all blog posts are selected, deselect all
        deselectAllBlogPosts()
      } else {
        // Otherwise, select all blog posts
        selectAllBlogPosts(blogPostsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <NewspaperIcon className="h-5 w-5" />
                {t('blog_posts')}
              </CardTitle>
              <CardDescription>{t('blog_posts_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/blog-posts/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_blog_post')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedBlogPostIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedBlogPostIds.length})</span>
                  </div>
                </Button>
              )}
              <BlogPostsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {blogPostsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  blogPostsData &&
                                  blogPostsData.items.length > 0 &&
                                  selectedBlogPostIds.length === blogPostsData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t('picture')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy={`translations.${locale}.title`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('title')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blogPostsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        blogPostsData.items.map((blogPost, index) => (
                          <ContextMenu key={blogPost.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(blogPost.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isBlogPostSelected(blogPost.id)}
                                        onCheckedChange={() => toggleBlogPostSelection(blogPost.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {blogPost.mainPicture ? (
                                    <Image
                                      src={getResizedPictureUrl(blogPost.mainPicture.path, 90, 90)}
                                      alt={blogPost.mainPicture.name || ''}
                                      width={90}
                                      height={90}
                                      className="mx-auto max-h-22.5 w-auto border object-contain"
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <FileIcon className="text-muted-foreground h-10 w-10" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>
                                      {textLimit(getLocalizedValue(blogPost, 'title', locale, defaultLocale), 120)}
                                    </span>
                                    <span className="text-muted-foreground text-xs">ID: {blogPost.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(blogPost.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(blogPost.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(blogPost.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(blogPost.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={blogPostsData.page}
                  totalPages={blogPostsData.pages}
                  totalItems={blogPostsData.total}
                  pageSize={blogPostsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteBlogPostId}
          onCancel={() => setDeleteBlogPostId(null)}
          onConfirm={confirmDeleteBlogPost}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteBlogPosts}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
